import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DynamicServiceDetailsForm } from '../components/service-details';
import Spinner from '../components/common/loader';
import {
  submitBooking,
  submitUserDetails,
  startLoading,
  getShopInfo,
} from '../state/actions/service';
import '../styles/service-description.scss';

function DynamicServiceDetails({ action = 'standard' }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const service = useSelector((state) => state.service);
  const { shopid } = useParams();
  const { t, i18n } = useTranslation();

  // Language handling (mirrors dynamic-event-details-view.js)
  const [resolvedLanguage, setLanguage] = useState(
    localStorage.getItem('language') || i18n.resolvedLanguage
  );
  useEffect(() => {
    if (resolvedLanguage) {
      i18n.changeLanguage(resolvedLanguage);
      localStorage.setItem('language', resolvedLanguage);
    }
  }, [resolvedLanguage, i18n]);
  useEffect(() => {
    const handleLanguageChange = () => setLanguage(i18n.resolvedLanguage);
    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, [i18n]);

  const typeId = action === 'Rental_out' ? 3 : action === 'Rental_in' ? 2 : 1;

  const onFormSubmitHandler = (data) => {
    dispatch(startLoading());
    dispatch(submitUserDetails(data));
    service.user = data;
    dispatch(submitBooking(service, history, typeId));
  };

  if (service.shop === undefined) {
    dispatch(getShopInfo(shopid, typeId));
  }

  return (
    <div className="container">
      {service.loading ? (
        <div className="loading-spinner">
          <Spinner />
          <span className="loading-text">{t('loading', 'Loading')}</span>
        </div>
      ) : (
        <>
          <div className="language-switch">
            <button
              type="button"
              className={`language-option ${resolvedLanguage === 'sv' ? 'active' : ''}`}
              onClick={() => setLanguage('sv')}
            >
              Svenska
            </button>
            <button
              type="button"
              className={`language-option ${resolvedLanguage === 'en' ? 'active' : ''}`}
              onClick={() => setLanguage('en')}
            >
              English
            </button>
          </div>
          <ServiceDescription shop={service.shop} action={action} />
          <DynamicServiceDetailsForm
            action={action}
            onSubmit={onFormSubmitHandler}
            schedules={service.schedules}
            slots={service.slots}
            isLoading={service.loading}
            errorMessage={service.error}
          />
          <div className="privacy-policy">
            <div>
              {t('privacy_policy_accept', 'I hereby accept MIOO Cycling’s')}
              <br />
              <div className="text-center">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://mioo.cc/sv/om-mioo/integritetspolicy"
                >
                  {t('privacy_policy_link', 'Privacy Policy.')}
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ServiceDescription({ shop, action }) {
  const { t } = useTranslation();
  // Use translation keys with fallbacks containing interpolation (correct i18next signature)
  const introText =
    action === 'Rental_out'
      ? t('rental_out_intro', {
          defaultValue: 'Här bokar du tid för upphämtning av din cykel hos {{name}}, {{address}}.',
          name: shop?.name || '',
          address: shop?.address || '',
        })
      : action === 'Rental_in'
      ? t('rental_in_intro', {
          defaultValue: 'Här bokar du tid för återlämning av din cykel hos {{name}}, {{address}}.',
          name: shop?.name || '',
          address: shop?.address || '',
        })
      : t('standard_intro', {
          defaultValue: 'Här bokar du tid för inlämning av din cykel hos {{name}}, {{address}}.',
          name: shop?.name || '',
          address: shop?.address || '',
        });
  const infoText =
    action === 'Rental_out'
      ? t('rental_out_info', { defaultValue: 'Du hämtar cykeln på din bokade tid.' })
      : action === 'Rental_in'
      ? t('rental_in_info', { defaultValue: 'Du lämnar tillbaka cykeln på din bokade tid.' })
      : t('standard_info', {
          defaultValue:
            'Du lämnar in cykeln på din bokade tid och får all övrig information på plats.',
        });

  return (
    <div className="service-description">
      <h1>{t('Book_time', 'Boka tid')}</h1>
      <div className="break-line" />
      <div>
        <p>{introText}</p>
        <p>{infoText}</p>
      </div>
    </div>
  );
}

export default DynamicServiceDetails;
